import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, Sparkles, ArrowRight, IndianRupee, Layers, ShieldCheck, Infinity as InfinityIcon, Star, Loader2
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import CheckoutModal from '../components/client/CheckoutModal';

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axiosClient.get('/plans');
        const normalizedPlans = (res.data.plans ?? []).map(plan => {
          let parsedFeatures = [];
          if (Array.isArray(plan.features)) {
            parsedFeatures = plan.features;
          } else if (typeof plan.features === 'string') {
            try { parsedFeatures = JSON.parse(plan.features); } catch(e) {}
          }
          return {
            ...plan,
            features: parsedFeatures,
            price: plan.price ?? '',
            period: plan.period ?? ''
          };
        });
        setPlans(normalizedPlans);
      } catch (err) {
        console.error('Failed to fetch pricing plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="section-wrapper" style={{ paddingBottom: '4rem' }}>
        <div className="grid-2">
          <div>
            <span className="section-label">
              <Sparkles size={14} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
              Flexible Pricing
            </span>
            <h1 className="hero-gradient-text" style={{ fontSize: '3rem' }}>
              Choose the Perfect Plan for Your School
            </h1>
            <p className="section-desc" style={{ fontSize: '1.2rem', marginBottom: '2.5rem' }}>
              Transparent pricing with no hidden fees. Scale as you grow and choose the plan that fits your institution.
            </p>
          </div>

          <div className="hero-mockup-wrapper" style={{ padding: '2rem' }}>
            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'center', margin: 0 }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <IndianRupee size={32} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>Simple Pricing</h3>
                <p style={{ margin: 0, fontSize: '0.95rem' }}>Simple, per-student pricing built to fit schools of every size without any hidden costs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section style={{ borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', background: 'rgba(241, 245, 249, 0.2)' }}>
        <div className="section-wrapper">
          <div className="pricing-header">
            <span className="section-label">Plans</span>
            <h2 className="section-title">Transparent pricing with no hidden fees</h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
              {plans.map((plan, index) => (
                <div
                  key={plan.id}
                  className={`relative rounded-[2rem] p-10 flex flex-col cursor-pointer transition-all duration-300 hover:-translate-y-2 ${
                    plan.popular 
                      ? 'shadow-[0_20px_40px_-15px_rgba(37,99,235,0.4)] scale-105 border-4 border-primary-300/30 z-10 hover:shadow-[0_25px_50px_-12px_rgba(37,99,235,0.5)] text-white' 
                      : 'bg-[#f8fafc] border border-[#e2e8f0] shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] hover:shadow-[0_20px_40px_-15px_rgba(6,81,237,0.2)]'
                  }`}
                  style={plan.popular ? { background: 'var(--gradient-primary)', transform: 'scale(1.05)' } : {}}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#ff8c00] text-white text-[0.75rem] font-bold px-5 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1 shadow-md">
                      <Star size={14} className="fill-white" /> MOST POPULAR
                    </div>
                  )}
                  
                  <h3 className={`text-[1.7rem] font-bold mb-2 ${plan.popular ? 'text-white' : 'text-[#0f172a]'}`}>{plan.name}</h3>
                  <p className={`text-[0.95rem] mb-6 min-h-[40px] ${plan.popular ? 'text-[#e0f2fe]' : 'text-[#64748b]'}`}>{plan.description}</p>
                  
                  <div className="flex flex-col mb-8">
                    <div className="flex items-baseline gap-1">
                      <span className={`font-extrabold leading-none tracking-tight ${plan.popular ? 'text-white' : 'text-[#0f172a]'} ${plan.price === 'Custom' ? 'text-[2.5rem]' : 'text-[3rem]'}`}>{plan.price}</span>
                      {plan.price !== 'Custom' && (
                        <span className={`text-[0.95rem] font-medium ${plan.popular ? 'text-[#bae6fd]' : 'text-[#64748b]'}`}>{plan.period}</span>
                      )}
                    </div>
                    {plan.period === '/yr' && (
                      <span className="text-[0.85rem] font-semibold text-emerald-500 mt-2">Save annually</span>
                    )}
                    {plan.price === 'Custom' && (
                      <span className="text-[0.85rem] font-semibold text-[#64748b] mt-2">Unlimited students</span>
                    )}
                    {plan.price !== 'Custom' && plan.period !== '/yr' && (
                      <span className={`text-[0.85rem] font-semibold mt-2 ${plan.popular ? 'text-[#bae6fd]' : 'text-[#64748b]'}`}>Minimum 100 students</span>
                    )}
                  </div>
                  
                  <ul className="space-y-4 mb-10 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Check size={20} className={`shrink-0 ${plan.popular ? 'text-[#bae6fd]' : 'text-[#3b82f6]'}`} strokeWidth={2.5} />
                        <span className={`text-[0.95rem] ${plan.popular ? 'text-white' : 'text-[#475569]'}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    onClick={() => {
                      if (plan.price !== 'Custom') {
                        setSelectedPlan(plan);
                      } else {
                        window.location.href = '/contact';
                      }
                    }}
                    className={`w-full py-3.5 rounded-[0.85rem] font-semibold transition-colors shadow-sm ${
                      plan.popular 
                        ? 'bg-white text-primary-600 hover:bg-slate-50' 
                        : 'bg-[#0f172a] text-white hover:bg-slate-800'
                    }`}
                  >
                    {plan.price === 'Custom' ? 'Contact Us' : 'Get Started'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Self-Hosted Section */}
      <section className="section-wrapper">
        <div className="grid-2">
          <div>
            <span className="section-label">Self-Hosted Option</span>
            <h2 className="section-title">Looking for a Self-Hosted Version?</h2>
            <p className="section-desc">
              Host on your own server and get full control over your data, with lifetime access and complete customization.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Get Self-Hosted Version
              <ArrowRight size={18} />
            </Link>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <Layers size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>Unlimited Users</h3>
                <p style={{ margin: 0, fontSize: '0.92rem' }}>No per-user limits for your whole institution. Add staff and students freely.</p>
              </div>
            </div>

            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <ShieldCheck size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>Complete Control</h3>
                <p style={{ margin: 0, fontSize: '0.92rem' }}>Deploy on your private servers, giving you complete data privacy and configuration control.</p>
              </div>
            </div>

            <div className="premium-card" style={{ padding: '2rem', display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
              <div className="icon-box" style={{ margin: 0, flexShrink: 0 }}>
                <InfinityIcon size={22} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>Lifetime Access</h3>
                <p style={{ margin: 0, fontSize: '0.92rem' }}>One-time payment model for self-hosted deployment. No recurring licensing fees.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trial CTA Footer */}
      <section style={{ background: 'var(--gradient-primary)', color: '#ffffff', borderTop: '1px solid var(--border-color)' }}>
        <div className="section-wrapper" style={{ textAlign: 'center', padding: '6rem 1.5rem' }}>
          <span className="section-label" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', color: '#ffffff' }}>
            Start Now
          </span>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#ffffff', marginBottom: '1.5rem', fontFamily: 'var(--font-heading)' }}>
            Start Your 30-Day Free Trial
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 2.5rem auto' }}>
            No credit card required. Full access to all features and priority onboarding support.
          </p>
          <Link to="/contact" className="btn btn-outline btn-lg" style={{ background: '#ffffff', color: 'var(--primary)', border: 'none' }}>
            Get Started Now
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
      {/* Checkout Modal */}
      <CheckoutModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
    </div>
  );
}
